import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type DashboardToolModalId =
  | 'profile'
  | 'url-editor'
  | 'remove-duplicates'
  | 'prefix-suffix'
  | 'stream-format'
  | 'guide'
  | 'create-channel'
  | 'create-category'
  | 'name-optimizer'
  | 'search-replace-name'
  | 'case-convert'
  | 'show-only-duplicates'
  | 'move-channels-playlist'
  | 'copy-channels-playlist'
  | 'epg-countries'
  | 'epg-external'
  | 'epg-settings'
  | 'sort-streams-categories'
  | 'epg-assignment-assistant'
  | 'epg-creator';

type Ctx = {
  active: DashboardToolModalId | null;
  openToolModal: (id: DashboardToolModalId) => void;
  closeToolModal: () => void;
};

const DashboardModalsContext = createContext<Ctx | null>(null);

export function DashboardModalsProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<DashboardToolModalId | null>(null);

  const openToolModal = useCallback((id: DashboardToolModalId) => setActive(id), []);
  const closeToolModal = useCallback(() => setActive(null), []);

  const value = useMemo(
    () => ({ active, openToolModal, closeToolModal }),
    [active, openToolModal, closeToolModal],
  );

  return <DashboardModalsContext.Provider value={value}>{children}</DashboardModalsContext.Provider>;
}

export function useDashboardToolModals(): Ctx {
  const ctx = useContext(DashboardModalsContext);
  if (!ctx) throw new Error('useDashboardToolModals requires DashboardModalsProvider');
  return ctx;
}
